import React from 'react';
import {Button} from '@interstate/components/Button';
import type {PortfolioOption} from '../lib/portfolio-nav';
import type {ViewId} from './dashboard';

const VIEWS: {id: ViewId; label: string}[] = [
  {id: 'portfolio', label: 'Portfolio'},
  {id: 'solutions', label: 'Solutions'},
  {id: 'kanban', label: 'Kanban'},
  {id: 'matrix', label: '2×2'},
  {id: 'fromto', label: 'From→To'}
];

/**
 * The single sticky top bar: program brand, portfolio segmented switcher, the five view tabs, and the
 * ☰ Menu button. Replaces the separate `Dashboard.tsx` header and `PortfolioDashboard.tsx` tab strip
 * so the active view survives a portfolio switch (FR-007) — this component owns neither piece of
 * state; both are passed down from `Dashboard`.
 *
 * Styled with inline design tokens rather than Tailwind utilities, matching the reference screenshot
 * (`docs/examples/Screenshot 2026-07-27 at 3.03.12 PM.png`) and the rest of the dashboard
 * (`PortfolioView`, `StatCard`), which were ported from `local/chris-v2/docs/index.html`. Tailwind's
 * gray ramp and radius scale don't land on this palette, so mixing the two read as two design
 * systems stacked on top of each other.
 */
export function DashboardHeader({
  activeView,
  onSelectView,
  options,
  selection,
  onSelectPortfolio,
  onOpenMenu
}: {
  activeView: ViewId;
  onSelectView: (view: ViewId) => void;
  options: PortfolioOption[];
  selection: string;
  onSelectPortfolio: (value: string) => void;
  onOpenMenu: () => void;
}): React.ReactElement {
  return (
    <header
      style={{
        background: '#fff',
        borderBottom: '1px solid #E2E8F0',
        position: 'sticky',
        top: 0,
        zIndex: 50
      }}
    >
      {/* maxWidth/padding match PortfolioDashboard's content box so the chrome lines up with the
          page beneath it; everything inside is left-clustered per the design. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 32,
          height: 56,
          maxWidth: 1400,
          margin: '0 auto',
          padding: '0 48px'
        }}
      >
        <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <span style={{fontSize: 14, color: '#fff'}}>⚡</span>
          </div>
          <div>
            <div style={{fontSize: 13, fontWeight: 700, color: '#0F172A', letterSpacing: '-0.01em', whiteSpace: 'nowrap'}}>
              Specialized Teams AIT Tracker
            </div>
            <div style={{fontSize: 10, color: '#94A3B8', fontFamily: "'DM Mono', monospace"}}>Journey Dashboard</div>
          </div>
        </div>

        <nav role="tablist" aria-label="Dashboard view" style={{display: 'flex', gap: 2}}>
          {VIEWS.map(v => {
            const active = activeView === v.id;
            return (
              <button
                key={v.id}
                role="tab"
                aria-selected={active}
                onClick={() => onSelectView(v.id)}
                style={{
                  padding: '6px 14px',
                  fontSize: 12,
                  fontWeight: active ? 600 : 400,
                  background: active ? '#F1F5F9' : 'transparent',
                  border: 'none',
                  borderRadius: 6,
                  color: active ? '#0F172A' : '#64748B',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                {v.label}
              </button>
            );
          })}
        </nav>

        {/* The switcher sits right, grouped with ☰ Menu: the view tabs are the primary navigation and
            read straight off the brand, while portfolio scope belongs with the other context controls.
            The flexible space then falls in the middle rather than the right edge, which is what made
            the first arrangement feel cramped. */}
        <div style={{marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16}}>
          {options.length > 0 && (
            <div
              role="group"
              aria-label="Portfolio"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                background: '#F1F5F9',
                borderRadius: 10,
                padding: 3
              }}
            >
              {options.map(o => {
                const active = selection === o.value;
                return (
                  <button
                    key={o.value}
                    onClick={() => onSelectPortfolio(o.value)}
                    aria-pressed={active}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '5px 12px',
                      fontSize: 12,
                      fontWeight: 700,
                      borderRadius: 8,
                      border: 'none',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      background: active ? '#fff' : 'transparent',
                      // labelColor, not color: the bright brand colours are for the dot — several are
                      // far too light to set 12px text in against white.
                      color: active ? (o.labelColor ?? '#0F172A') : '#64748B',
                      boxShadow: active ? '0 1px 2px rgba(15, 23, 42, 0.08)' : 'none'
                    }}
                  >
                    {o.color && (
                      <span style={{width: 8, height: 8, borderRadius: '50%', background: o.color, flexShrink: 0}} />
                    )}
                    {o.label}
                  </button>
                );
              })}
            </div>
          )}

          <Button buttonStyle="secondary" size="small" onClick={onOpenMenu}>
            ☰ Menu
          </Button>
        </div>
      </div>
    </header>
  );
}
